import React from "react";
import { MdArrowBack, MdInfo } from "react-icons/md";
import useLoginCredentialsForm from "./useLoginCredentialsForm";
import TextField from "./TextField";
import PasswordField from "./PasswordField.js";
import PasswordStrength from "./PasswordStrength.js";
import RoleSelector from "./RoleSelector.js";

const PASSWORD_HELP = "Must include upper, lower, digit, symbol (8–128).";

const LoginCredentialForm = ({
  onSubmit,
  onBack,
  includeRole = true,
  defaultRoles,
  submitting = false,
  includeEmail = true,
  submitLabel = "Create User",
}) => {
  const {
    formData,
    setField,
    markTouched,
    roles,
    toggleRole,
    selectAll,
    clearAll,
    errors,
    shouldShow,
    strength,
    canSubmit,
    buildPayload,
    setShowErrors,
  } = useLoginCredentialsForm({
    includeRole,
    defaultRoles,
    includeEmail,
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setShowErrors(true);
    if (!canSubmit) return;
    onSubmit?.(buildPayload());
  };

  const emailInvalid = shouldShow.email && errors.email;
  const passwordInvalid = shouldShow.password && errors.password;
  const confirmInvalid = shouldShow.confirm && errors.confirm;
  const showRoleError = includeRole && errors.roles && (shouldShow.password || shouldShow.confirm || shouldShow.email);

  return (
    <section className="ui-panel">
      <header className="ui-panel__header">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="ui-title mb-0">Login Credentials</h3>
          <span className="text-muted small d-flex align-items-center gap-1">
            <MdInfo /> Create secure access for the user
          </span>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-3 p-md-4">
        {includeEmail && (
          <TextField
            label="Username or Email"
            name="username"
            value={formData.username}
            onChange={(event) => setField("username", event.target.value)}
            onBlur={() => markTouched("username")}
            placeholder="Enter username or email"
            autoComplete="username"
            disabled={submitting}
            invalid={emailInvalid}
            invalidText="Please enter a valid email (max 190 chars)."
          />
        )}

        <PasswordField
          label="Password"
          name="password"
          value={formData.password}
          onChange={(event) => setField("password", event.target.value)}
          onBlur={() => markTouched("password")}
          placeholder="Enter password"
          autoComplete="new-password"
          disabled={submitting}
          invalid={passwordInvalid}
          invalidText={PASSWORD_HELP}
        />

        <PasswordField
          label="Confirm Password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={(event) => setField("confirmPassword", event.target.value)}
          onBlur={() => markTouched("confirmPassword")}
          placeholder="Confirm password"
          autoComplete="new-password"
          disabled={submitting}
          invalid={confirmInvalid}
          invalidText="Passwords must match."
        />

        {formData.password && <PasswordStrength strength={strength} />}

        {includeRole && (
          <RoleSelector
            roles={roles}
            onToggle={toggleRole}
            onSelectAll={selectAll}
            onClear={clearAll}
            disabled={submitting}
            invalid={showRoleError}
            invalidText="Select at least one role."
          />
        )}

        <div className="ui-actions mt-3 w-100 justify-content-between">
          <button
            type="button"
            className="ui-btn"
            onClick={onBack}
            disabled={submitting}
          >
            <MdArrowBack />
            Prev
          </button>
          <button
            type="submit"
            className="btn btn-primary rounded-pill d-flex align-items-center gap-2"
            disabled={submitting}
          >
            {submitting && (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            )}
            {submitLabel}
          </button>
        </div>
      </form>
    </section>
  );
};

export default LoginCredentialForm;
